import React, { useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';

import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';

// MODALES EXISTENTES (CON RUTAS CORREGIDAS AL 100%)
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';
import ModalConfirmacion from '../../../ui/ModalConfirmacion.jsx';
import ModalEndoso from '../../../ui/ModalEndoso.jsx'; 
import ModalNotificacion from '../../../ui/ModalNotificacion.jsx';

// FILTROS
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';

// IMPORTACIONES DE ARQUITECTURA MODULAR
import { useEndososTable } from './useEndososTable';
import { getPersonaChipClass, getEndosoStatusChip } from '../../../../utils/styles.js';

// NUESTRO CENTRALIZADOR DE ICONOS VECTORIALES UNIFICADOS
import { 
    IconArrowLeft, 
    IconEdit, 
    IconUser, 
    IconPolicy, 
    IconTrash, 
    IconCompany, 
    IconLock 
} from '../common/Icons';

const EndosoRow = memo(({ item, isLocked, isLockedByMe, isExpanded, columnVisibility, onToggleExpand, onAction, onUnlock }) => {
    const activeColumnsCount = useMemo(() => {
        const toggledCount = Object.values(columnVisibility).filter(v => v).length;
        return toggledCount + 2; 
    }, [columnVisibility]);

    return (
        <>
            <tr 
                onClick={() => !isLocked && onToggleExpand(item.id)} 
                className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                    ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''}
                    ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
            >
                <td className={`p-2.5 w-8 text-center border-b border-r border-slate-100 sticky left-0 z-[45] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                    <svg className={`w-3.5 h-3.5 mx-auto transition-transform duration-300 ${isExpanded ? 'rotate-90 text-indigo-600' : 'text-slate-300 group-hover:text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                </td>

                {columnVisibility.fechaCarga && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-500 tabular-nums">
                        {item.fechaCarga?.split('-').reverse().join('/') || '-'}
                    </td>
                )}

                {columnVisibility.displayNombre && (
                    <td className="p-2.5 px-4 text-sm text-slate-900 font-bold border-b border-r border-slate-100 truncate max-w-[250px]">
                        {item.displayNombre}
                    </td>
                )}

                {columnVisibility.displayDoc && (
                    <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100">{item.displayDoc}</td>
                )}

                {columnVisibility.nPoliza && (
                    <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">{item.nPoliza || '-'}</td>
                )}

                {columnVisibility.compania && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                        <div className="flex items-center justify-center">
                            <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg w-fit group-hover:bg-white transition-colors shadow-sm">
                                <div className="text-blue-400"><IconCompany /></div>
                                <span className="text-[11px] text-blue-800 font-bold uppercase truncate max-w-[140px]">{item.compania || '-'}</span>
                            </div>
                        </div>
                    </td>
                )}

                {columnVisibility.riesgo && (
                    <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                        <div className="flex flex-col text-left">
                            <span className="font-bold text-slate-800 tracking-tight">{item.riesgo || '-'}</span>
                            <span className="text-[11px] font-medium text-slate-500 uppercase">{item.subriesgo || 'General'}</span>
                        </div>
                    </td>
                )}

                {columnVisibility.type && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-[10px] font-black uppercase text-indigo-600 bg-indigo-50/30">
                        {item.tipoGestion || '-'}
                    </td>
                )}

                {columnVisibility.quienGestiono && (
                    <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                        {item.quienGestiono ? (
                            <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(item.quienGestiono)}`}>
                                <span className="text-[10px] font-bold uppercase">{item.quienGestiono}</span>
                            </div>
                        ) : '-'}
                    </td>
                )}

                {columnVisibility.tramite && (
                    <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100">{item.tramite || '-'}</td>
                )}

                {columnVisibility.estadoGestion && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${getEndosoStatusChip((item.estadoGestion || 'PENDIETNE').toUpperCase())}`}>
                            {item.estadoGestion || 'PENDIENTE'}
                        </span>
                    </td>
                )}

                {columnVisibility.canal && (
                    <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                        <div className="flex flex-col text-left">
                            <span className="font-bold text-slate-800 tracking-tight">{item.canal || '-'}</span>
                            <span className="text-[11px] font-medium text-slate-500 uppercase">{item.displayCanalNombre || '-'}</span>
                        </div>
                    </td>
                )}

                {/* Acciones de Fila */}
                <td className={`p-2.5 px-4 text-right sticky right-0 transition-colors z-20 border-b border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                    <div className="flex justify-end items-center gap-1.5">
                        <button onClick={(e) => { e.stopPropagation(); onAction(item, 'ficha'); }} className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all" title="Ficha Cliente"><IconUser /></button>
                        <button onClick={(e) => { e.stopPropagation(); onAction(item, 'poliza'); }} className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all" title="Ver Póliza Raíz"><IconPolicy /></button>
                        
                        {isLocked ? (
                            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                                <IconLock /> <span className="text-[9px] font-black uppercase tracking-tighter">{item.bloqueo?.userName || 'Ocupado'}</span>
                            </div>
                        ) : (
                            <>
                                <button onClick={(e) => { e.stopPropagation(); onAction(item, 'edit'); }} className="p-1.5 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Editar Endoso"><IconEdit /></button>
                                <button onClick={(e) => { e.stopPropagation(); onAction(item, 'delete'); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Eliminar Endoso"><IconTrash /></button>
                                {isLockedByMe && (
                                    <button onClick={(e) => { e.stopPropagation(); onUnlock(item.path); }} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </td>
            </tr>

            {/* Sub-fila Desplegable (Detalles del Endoso) */}
            {isExpanded && !isLocked && (
                <tr className="bg-slate-50/80 animate-fade-in border-b border-slate-200 shadow-inner">
                    <td colSpan={activeColumnsCount} className="p-0 overflow-hidden relative">
                         <div className="sticky left-0 w-[calc(100vw-120px)] p-8 md:p-12 text-left">
                            <div className="flex flex-col gap-2 max-w-4xl text-left">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-left">Observaciones del Endoso</span>
                                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm min-h-[80px] text-left">
                                    <p className="text-sm text-slate-600 italic leading-relaxed whitespace-pre-wrap text-left">
                                        {item.observaciones || "Sin observaciones registradas."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
});

EndosoRow.displayName = 'EndosoRow';

// --- ORQUESTADOR MÁSTER ---
const GestionEndosos = () => {
    const navigate = useNavigate();
    
    // --- ◄ MEJORA ADAPTATIVA SENIOR: Consumo de la capa lógica y de datos limpia ---
    const { state, actions, tableState } = useEndososTable();

    const {
        loading,
        user,
        clientsCache,
        activeItem,
        activePolizaData,
        showEditModal,
        showPolicyModal,
        showFichaModal,
        showDeleteConfirm,
        alertConfig,
        itemsTable
    } = state;

    const {
        setAlertConfig,
        setShowFichaModal,
        setShowPolicyModal,
        setShowEditModal,
        setShowDeleteConfirm,
        handleAction,
        handleSuccess,
        confirmDelete,
        unlockPoliza,
        setActiveItem,
        setActivePolizaData
    } = actions;

    // --- ◄ SOLUCIÓN DEL BUG DE LÍNEAS INDEFINIDAS DE ESLINT ---
    // Desestructuramos del tableState para que todas las funciones y estados de la tabla queden definidos en la UI
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        expandedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        toggleRowExpansion,
        checkIsLocked,
        setCurrentPage
    } = tableState;

    const columnLabels = {
        fechaCarga: "Fecha Carga", displayNombre: "Cliente", displayDoc: "DNI/CUIT",
        nPoliza: "Póliza", compania: "Compañía", riesgo: "Riesgo/Sub", type: "Gestión",
        quienGestiono: "Responsable", tramite: "Trámite", estadoGestion: "Estado", canal: "Canal"
    };

    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const filteredByRiesgo = filterValues.riesgo?.length > 0 
            ? itemsTable.filter(i => filterValues.riesgo.includes(i.riesgo)) 
            : itemsTable;

        const filteredByCanal = filterValues.canal?.length > 0
            ? itemsTable.filter(i => filterValues.canal.includes(i.canal))
            : itemsTable;

        return [
            { name: 'search', label: 'Buscador Global', type: 'text', placeholder: 'Cliente, DNI, Póliza o Trámite...', className: 'w-80' },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique('riesgo') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: getUnique('subriesgo', filteredByRiesgo) },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique('compania') },
            { name: 'tipoGestion', label: 'Gestión', type: 'select', isMulti: true, options: getUnique('tipoGestion') },
            { name: 'quienGestiono', label: 'Responsable', type: 'select', isMulti: true, options: getUnique('quienGestiono') },
            { name: 'estadoGestion', label: 'Estado', type: 'select', isMulti: true, options: getUnique('estadoGestion') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: getUnique('displayCanalNombre', filteredByCanal) },
            { name: 'subcanal', label: 'Subcanal', type: 'select', isMulti: true, options: getUnique('subcanal', filteredByCanal) },
            { name: 'fecha', label: 'Fecha Carga', type: 'date-range' },
        ];
    }, [itemsTable, filterValues.riesgo, filterValues.canal]);

    if (loading) return (
        <div className="flex h-screen flex-col items-center justify-center bg-white space-y-4">
            <div className="w-12 h-12 border-4 border-indigo-950/20 border-t-indigo-950 rounded-full animate-spin"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-950">Sincronizando Tablero</p>
        </div>
    );

    return (
        <div className="flex h-screen w-full flex-col overflow-hidden bg-white font-['Inter'] antialiased text-left text-slate-900">
            <div className="h-1 w-full bg-indigo-900 shrink-0"></div>
            
            {/* CABECERA DECLARATIVA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6 text-left">
                    <button onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><IconArrowLeft /></button>
                    <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight text-left">Gestión de Endosos</h2>
                </div>
                <div className="flex items-center gap-4 text-left">
                    {/* Selector de visibilidad de columnas común */}
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm text-left">
                        <div className="bg-indigo-900 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500 text-left">Trámites Activos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS */}
            {/* ◄ SOLUCIÓN BUG FILTRO VACÍO: Estampamos una KEY estática y estable para evitar que React destruya e inicialice en blanco el componente al descargar los endosos */}
            <FiltrosDinamicos 
                key="endosos-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE */}
            {}
            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="3200px">
                    <thead>
                        <tr className="bg-slate-900 text-white text-left">
                            <th className="w-8 border-b border-r border-slate-700 bg-slate-900 sticky left-0 z-[60] top-0 shadow-[2px_0_5px_rgba(0,0,0,0.2)]"></th>
                            <SortableHeader label="Fecha Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[200px]" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-center" />
                            <SortableHeader label="Riesgo / Sub" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[150px]" />
                            <SortableHeader label="Tipo Gestión" sortKey="type" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Responsable" sortKey="quienGestiono" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="N° Trámite" sortKey="tramite" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Estado" sortKey="estadoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Canal / Nombre" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32" />
                            <th className="w-24 p-4 sticky right-0 top-0 z-[65] bg-slate-900 border-b border-slate-700 uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.map(item => {
                            const isLocked = checkIsLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <EndosoRow 
                                    key={item.id} 
                                    item={item} 
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    isExpanded={expandedRowId === item.id}
                                    columnVisibility={columnVisibility}
                                    onToggleExpand={toggleRowExpansion}
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza}
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN UNIVERSAL */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="trámites"
                    accentClass="text-indigo-900 bg-indigo-50 border-indigo-100"
                />
            </main>

            {/* MODALES */}
            {activeItem && (
                <>
                    <ModalFichaCliente 
                        show={showFichaModal} 
                        onClose={() => { setShowFichaModal(false); setActiveItem(null); }} 
                        client={clientsCache[activeItem.clienteId] || { id: activeItem.clienteId }} 
                    />
                    <ModalDetallePoliza 
                        show={showPolicyModal} 
                        onClose={() => { setShowPolicyModal(false); setActivePolizaData(null); setActiveItem(null); }} 
                        poliza={activePolizaData} 
                    />
                    <ModalEndoso 
                        show={showEditModal} 
                        onClose={() => { unlockPoliza(activeItem.path); setShowEditModal(false); setActiveItem(null); setActivePolizaData(null); }} 
                        onSuccess={handleSuccess}
                        poliza={activePolizaData} 
                        clientData={clientsCache[activeItem.clienteId] || { id: activeItem.clienteId }} 
                        initialData={activeItem} 
                    />
                    <ModalConfirmacion 
                        show={showDeleteConfirm} 
                        onClose={() => { setShowDeleteConfirm(false); setActiveItem(null); }} 
                        onConfirm={confirmDelete} 
                        title="Eliminar Endoso" 
                        message="¿Estás seguro de eliminar este endoso? Esta acción es irreversible." 
                    />
                </>
            )}

            <ModalNotificacion 
                show={alertConfig.show}
                onClose={() => setAlertConfig({ ...alertConfig, show: false })}
                title={alertConfig.title}
                message={alertConfig.message}
                type={alertConfig.type}
            />
        </div>
    );
};

export default GestionEndosos;